import React, { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Server, Globe, ExternalLink, Plus, Minus, CircleCheck, CircleX, AlertTriangle } from 'lucide-react';
import { useWebsites } from '@/hooks/useWebsites';
import { useHostings } from '@/hooks/useHostings';
import { useWebsiteHostingMap } from '@/hooks/useWebsiteHostingMap';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useUptimeKuma } from '@/hooks/useUptimeKuma';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { cn } from '@/lib/utils';

interface HostingGroupsViewProps {
  onAssignClick: (websiteId: string) => void;
  onUnassignClick: (mappingId: string) => Promise<void>;
}

const HostingGroupsView: React.FC<HostingGroupsViewProps> = ({ onAssignClick, onUnassignClick }) => {
  const { websites: allWebsites } = useWebsites();
  const { workspace } = useWorkspace();
  // Widok podąża za globalnym przełącznikiem workspace — w trybie Firma nie
  // pokazujemy domen Sponsoraki (i odwrotnie). Zespół i tak dostaje z RLS samą firmę.
  const websites = useMemo(
    () => allWebsites.filter(w => (w.workspace || 'firma') === workspace),
    [allWebsites, workspace],
  );
  const { hostings } = useHostings();
  const { mappings, getMappingsForHosting } = useWebsiteHostingMap();
  const { getMonitorStatus } = useUptimeKuma();

  // Helper function to render monitoring status
  const renderMonitoringStatus = (websiteUrl: string) => {
    const domain = websiteUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
    const status = getMonitorStatus(domain);

    switch (status) {
      case 'online':
        return <CircleCheck className="h-4 w-4 text-green-500" />;
      case 'offline':
        return <CircleX className="h-4 w-4 text-red-500" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
    }
  };

  // Group websites by hosting
  const hostingGroups = useMemo(() => {
    const groups = hostings.map(hosting => {
      const hostingMappings = getMappingsForHosting(hosting.id);
      const assignedWebsites = hostingMappings.map(mapping => {
        const website = websites.find(w => w.id === mapping.website_id);
        return website ? { ...website, mappingId: mapping.id } : null;
      }).filter(Boolean);

      return {
        hosting,
        websites: assignedWebsites,
        count: assignedWebsites.length
      };
    });

    // Add unassigned websites group
    const assignedWebsiteIds = new Set(mappings.map(m => m.website_id));
    const unassignedWebsites = websites.filter(w => !assignedWebsiteIds.has(w.id));

    return {
      groups,
      unassignedWebsites
    };
  }, [hostings, websites, mappings, getMappingsForHosting]);

  // Calculate days until expiry
  const getDaysUntilExpiry = (expiryDate: string) => {
    if (!expiryDate) return Infinity;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(expiryDate);
    if (isNaN(expiry.getTime())) return Infinity;
    expiry.setHours(0, 0, 0, 0);
    const diffTime = expiry.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  return (
    <div className="space-y-4">
      {/* Hosting Groups */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {hostingGroups.groups.map(({ hosting, websites: assignedWebsites, count }) => (
          <Card key={hosting.id} className="border border-border/50 hover:border-primary/30 hover:shadow-lg transition-all duration-300 bg-gradient-to-br from-card to-card/50">
            <CardHeader className="pb-2.5 px-4 pt-3.5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                  <div className="p-1.5 bg-gradient-to-br from-primary/20 to-primary/5 rounded-lg ring-1 ring-primary/20">
                    <Server className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-sm font-semibold truncate" title={hosting.name}>
                        {hosting.name}
                      </CardTitle>
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-medium shrink-0">
                        {count}
                      </Badge>
                    </div>
                    {hosting.provider && (
                      <CardDescription className="text-[10px] mt-0.5">
                        {hosting.provider}
                      </CardDescription>
                    )}
                  </div>
                </div>
                {hosting.expiry_date && (() => {
                  const daysUntilExpiry = getDaysUntilExpiry(hosting.expiry_date);
                  const isExpired = daysUntilExpiry < 0;
                  const isExpiringSoon = daysUntilExpiry >= 0 && daysUntilExpiry <= 14;
                  
                  return (
                    <div className="flex flex-col items-end gap-0.5 shrink-0">
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "text-[10px] px-1.5 py-0 font-medium",
                          isExpired && "border-red-500/50 bg-red-500/5 text-red-600",
                          isExpiringSoon && "border-orange-500/50 bg-orange-500/5 text-orange-600",
                          !isExpired && !isExpiringSoon && "border-emerald-500/50 bg-emerald-500/5 text-emerald-600"
                        )}
                      >
                        {isExpired ? 'Wygasł' : 'Opłacony'}
                      </Badge>
                      <span className={cn(
                        "text-[10px] font-semibold",
                        isExpired && "text-red-600",
                        isExpiringSoon && "text-orange-600",
                        !isExpired && !isExpiringSoon && "text-emerald-600"
                      )}>
                        {isExpired && `${Math.abs(daysUntilExpiry)}d temu`}
                        {isExpiringSoon && `za ${daysUntilExpiry}d`}
                        {!isExpired && !isExpiringSoon && `${daysUntilExpiry}d`}
                      </span>
                    </div>
                  );
                })()}
              </div>
            </CardHeader>
            <CardContent className="px-4 pb-3">
              {assignedWebsites.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground text-xs border border-dashed rounded-lg bg-muted/20">
                  <Server className="h-6 w-6 mx-auto mb-1.5 opacity-40" />
                  <p className="font-medium">Brak przypisanych domen</p>
                  <p className="text-[10px] mt-0.5 opacity-70">Przypisz domeny używając tabeli</p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  {assignedWebsites.map((website: any) => {
                    const daysUntilExpiry = website.expiry_date ? getDaysUntilExpiry(website.expiry_date) : Infinity;
                    const isExpired = daysUntilExpiry < 0;
                    const isExpiringSoon = daysUntilExpiry >= 0 && daysUntilExpiry <= 14;

                    return (
                      <div
                        key={website.id}
                        className={cn(
                          "group relative flex items-center justify-between px-2.5 py-2 rounded-md border transition-all hover:shadow-sm hover:scale-[1.01]",
                          isExpired && "bg-red-500/5 border-red-500/20 hover:border-red-500/40",
                          isExpiringSoon && "bg-orange-500/5 border-orange-500/20 hover:border-orange-500/40",
                          !isExpired && !isExpiringSoon && "bg-card border-border/50 hover:border-primary/30"
                        )}
                      >
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {renderMonitoringStatus(website.url)}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="font-medium text-xs truncate" title={website.name}>
                                {website.name}
                              </span>
                              {website.category && (
                                <Badge variant="outline" className="text-[10px] px-1 py-0 shrink-0">
                                  {website.category}
                                </Badge>
                              )}
                            </div>
                            <a
                              href={website.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[10px] text-primary/70 hover:text-primary flex items-center gap-0.5 mt-0.5 truncate transition-colors"
                            >
                              <ExternalLink className="h-2.5 w-2.5 shrink-0" />
                              <span className="truncate">{website.url}</span>
                            </a>
                            {website.expiry_date && (isExpired || isExpiringSoon) && (
                              <div className="text-[10px] mt-1 font-semibold">
                                {isExpired && (
                                  <span className="text-red-600">
                                    ⚠ {Math.abs(daysUntilExpiry)}d temu
                                  </span>
                                )}
                                {isExpiringSoon && (
                                  <span className="text-orange-600">
                                    ⏰ za {daysUntilExpiry}d
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 w-6 p-0 text-red-500 hover:text-red-600 hover:bg-red-500/10 opacity-0 group-hover:opacity-100 transition-all shrink-0"
                              title="Odepnij domenę"
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Odepnij domenę</AlertDialogTitle>
                              <AlertDialogDescription>
                                Czy na pewno chcesz odpiąć <strong>{website.name}</strong> od hostingu <strong>{hosting.name}</strong>?
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Anuluj</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => onUnassignClick(website.mappingId)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Odepnij
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Unassigned Websites */}
      {hostingGroups.unassignedWebsites.length > 0 && (
        <Card className="border border-dashed border-muted-foreground/20 bg-muted/5">
          <CardHeader className="pb-2.5 px-4 pt-3.5">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 bg-muted/50 rounded-lg ring-1 ring-border/50">
                <Globe className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  Nieprzypisane domeny
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-medium">
                    {hostingGroups.unassignedWebsites.length}
                  </Badge>
                </CardTitle>
                <CardDescription className="text-[10px] mt-0.5">
                  Domeny bez przypisanego hostingu
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="px-4 pb-3">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
              {hostingGroups.unassignedWebsites.map((website) => {
                const daysUntilExpiry = website.expiry_date ? getDaysUntilExpiry(website.expiry_date) : Infinity;
                const isExpired = daysUntilExpiry < 0;
                const isExpiringSoon = daysUntilExpiry >= 0 && daysUntilExpiry <= 14;

                return (
                  <div
                    key={website.id}
                    className={cn(
                      "group relative flex items-center justify-between px-2.5 py-2 rounded-md border transition-all hover:shadow-sm hover:scale-[1.01]",
                      isExpired && "bg-red-500/5 border-red-500/20 hover:border-red-500/40",
                      isExpiringSoon && "bg-orange-500/5 border-orange-500/20 hover:border-orange-500/40",
                      !isExpired && !isExpiringSoon && "bg-card border-border/50 hover:border-primary/30"
                    )}
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {renderMonitoringStatus(website.url)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="font-medium text-xs truncate" title={website.name}>
                            {website.name}
                          </span>
                          {website.category && (
                            <Badge variant="outline" className="text-[10px] px-1 py-0 shrink-0">
                              {website.category}
                            </Badge>
                          )}
                        </div>
                        {website.expiry_date && (isExpired || isExpiringSoon) && (
                          <div className="text-[10px] mt-1 font-semibold">
                            {isExpired && (
                              <span className="text-red-600">
                                ⚠ {Math.abs(daysUntilExpiry)}d temu
                              </span>
                            )}
                            {isExpiringSoon && (
                              <span className="text-orange-600">
                                ⏰ za {daysUntilExpiry}d
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onAssignClick(website.id)}
                      className="h-6 w-6 p-0 text-primary hover:text-primary/80 hover:bg-primary/10 opacity-0 group-hover:opacity-100 transition-all shrink-0"
                      title="Przypisz do hostingu"
                    >
                      <Plus className="h-3 w-3" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default HostingGroupsView;

